import { Any } from "@cede/types";
import { Logger, LoggerType } from "../../logger";
import { Newable } from "./di.container.type";

export class DIContainer {
  public dependencies = new Map();

  private tempInstances = new Map();

  constructor(private logger?: LoggerType) {}

  bind<T>(symbol: symbol, dependency: Newable<T> | T) {
    this.dependencies.set(symbol, dependency);
  }

  getDependencies(symbols: symbol[]): Any[] {
    return symbols.map((symbol) => this.get(symbol));
  }

  setDependencies(dependencies: Map<symbol, Any>) {
    this.dependencies = new Map(dependencies);
  }

  get<T>(symbol: symbol): T {
    this.tempInstances = new Map();
    const instance = this._get<T>(symbol);
    // Lazy dependencies are injected at the end
    this._injectLazyDependencies();
    return instance;
  }

  _get<T>(symbol: symbol): T {
    const objectClass = this.dependencies.get(symbol);

    if (!objectClass) {
      throw new Error(`Dependency ${symbol.toString()} not found`);
    }
    //prevent from multiple instantiations of a same class
    if (!this._isClass(objectClass)) {
      return objectClass;
    }

    // get instance dependencies
    const dependencySymbols: symbol[] = objectClass.dependencies || [];

    const dependencies = dependencySymbols.map((dependencySymbol) => this._get(dependencySymbol));

    const instance = new objectClass(...dependencies);
    if (this.logger) {
      this.logger.log(`Construct ${symbol.toString()}`, Logger.types.INFO);
    }

    this.tempInstances.set(symbol, instance);
    this.dependencies.set(symbol, instance);

    return instance;
  }

  _injectLazyDependencies() {
    this.tempInstances.forEach((instance) => {
      const lazyDependencySymbols: symbol[] = instance.constructor.lazyDependencies;
      if (!lazyDependencySymbols || !lazyDependencySymbols.length) return;
      const dependencies = lazyDependencySymbols.map((dependencySymbol) => this._get(dependencySymbol));
      instance.setLazyDependencies(...dependencies);
    });
  }

  private _isClass(v: Any) {
    return typeof v === "function" && /^\s*class\s*/.test(v.toString());
  }
}
